// ── Programs ("/programs") — the program directory (Wave 2) ────────────────
// ONE honest read (the programs list) routed through useHonestQuery ->
// <HonestState>. The page NEVER hand-rolls state branching: a gate /
// permission-denied / unavailable / error response renders via <HonestState>
// (tagged data-honest-posture), NEVER as a plain empty table. Real content (the
// DataTable + filter/create toolbar) renders only on the ready-with-data (or
// honest-empty) path.
//
// State machine (PATCH status): draft -> active (PUBLISH) -> archived (ARCHIVE).
// Reads may emit a WIDER status set than create accepts (no DB CHECK — blueprint
// §1 trap 5); the table/detail humanize ANY status while writes only ever set
// draft|active|archived.
//
// ROLE-GATING (FR-PROG-002, release-critical):
//  - Super Admin / Admin -> full program CRUD + lifecycle affordances.
//  - Coach / Company Admin / Cohort Lead -> SCOPED READ context only (the route
//    manifest grants companies/programs READ to operationsRoles; the denied
//    write capability hides every create/edit/publish/archive affordance).
//  - PARTICIPANT -> DENIED THE PROGRAMS MODULE ENTIRELY: the route manifest's
//    `programs`/`program-detail` routes use operationsRoles, which EXCLUDES
//    participant (and cohort_lead) — so participant gets generic-not-found before
//    this page mounts AND the Programs nav item is absent (getNavigationRoutes
//    filters on evaluateRouteAccess). Participant program context appears only
//    through the Participant Dashboard (Wave 9), never this module.
//
// Affordances render ONLY when the read is READY (posture null) or honest-EMPTY
// (so filters can still be adjusted); a hard-denied read shows ONLY the honest
// state — no create/edit/publish/archive controls hanging over a refused surface.

import { Button, CollectionToolbar, IconDoc, PageFrame, ProTable, useToast } from "@vjal/ui";
import { useCallback, useMemo, useState } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { PROGRAMS_WITH_ARCHIVED_QUERY } from "../../services/coaching-catalog-services";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useOrganizationLifecycleMutations } from "../../hooks/useOrganizationLifecycleMutations";
import { asLiveApiId } from "../../hooks/useLiveProductData";
import { evaluateActionCapability } from "../../domain/capabilities";
import type { CoachingProgramDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useProgramsCollection } from "./programs-collection";
import { ProgramFormModal, type ProgramFormValues } from "./program-form-modal";
import { ProgramLifecycleModal, type ProgramLifecycleAction } from "./program-lifecycle-modal";

const services = getCoachingServices();

export function ProgramsPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useOrganizationLifecycleMutations();

  const allowCreate = evaluateActionCapability(role, "programs:create").allowed;
  const allowUpdate = evaluateActionCapability(role, "programs:update").allowed;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CoachingProgramDto | null>(null);
  const [lifecycle, setLifecycle] = useState<{ action: ProgramLifecycleAction; program: CoachingProgramDto } | null>(null);

  const openPublish = useCallback((row: CoachingProgramDto) => {
    setLifecycle({ action: "publish", program: row });
  }, []);

  const openArchive = useCallback((row: CoachingProgramDto) => {
    setLifecycle({ action: "archive", program: row });
  }, []);

  // ── The one honest read (archived programs included, F03) ───────────────────
  const query = useHonestQuery<ListResponse<CoachingProgramDto>>(
    coachingQueryKeys.list("programs.list", PROGRAMS_WITH_ARCHIVED_QUERY),
    () => services.programs.list(PROGRAMS_WITH_ARCHIVED_QUERY)
  );
  const items = query.data?.items;
  const allRows = useMemo(() => items ?? [], [items]);
  const collectionActions = useMemo(
    () => ({
      allowUpdate,
      onEdit: setEditTarget,
      onPublish: openPublish,
      onArchive: openArchive
    }),
    [allowUpdate, openPublish, openArchive]
  );
  const collection = useProgramsCollection(allRows, collectionActions, routePath);

  // useHonestQuery flags an empty `{items:[]}` as "empty"; once filters narrow a
  // non-empty list to zero we render the empty body ourselves (never a blank).
  const posture =
    query.posture === null && collection.total === 0 && allRows.length > 0 ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  // ── Writes (each toasts ONLY after the backend confirms; no false success) ──
  function submitCreate(values: ProgramFormValues) {
    return mutations.createProgram
      .mutateAsync(programCreateBody(values))
      .then((program) => {
        setCreateOpen(false);
        mutations.createProgram.reset();
        toast.success(`Program ${program.name ?? values.name} created`);
        return program;
      });
  }

  function submitEdit(values: ProgramFormValues) {
    if (!editTarget) return Promise.reject(new Error("No program selected"));
    return mutations.updateProgram
      .mutateAsync({ programId: editTarget.id, body: programEditBody(values) })
      .then((program) => {
        setEditTarget(null);
        mutations.updateProgram.reset();
        toast.success(`Program ${program.name ?? values.name} updated`);
        return program;
      });
  }

  function submitLifecycle() {
    if (!lifecycle) return Promise.reject(new Error("No program selected"));
    const { action, program } = lifecycle;
    const status = action === "publish" ? "active" : "archived";
    return mutations.updateProgram
      .mutateAsync({ programId: program.id, body: { name: program.name, status } })
      .then((updated) => {
        setLifecycle(null);
        mutations.updateProgram.reset();
        toast.success(`Program ${updated.name ?? program.name} ${action === "publish" ? "published" : "archived"}`);
        return updated;
      });
  }

  return (
    <PageFrame title="Programs" subtitle="Programs owned by companies in your scope." className="col-span-12">
      <section aria-label="Programs" className="flex flex-col gap-4" data-testid="programs-page">
        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by name, company, or coach…"
            primaryAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add program
                </Button>
              ) : undefined
            }
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-line bg-white shadow-scroll-hint">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              icon={<IconDoc size={22} />}
              canCreate={allowCreate && !showToolbar}
              title={posture === "empty" ? "No programs yet" : undefined}
              message={
                posture === "empty"
                  ? allowCreate
                    ? "Add the first program to start scoping cohorts."
                    : "Programs will appear here once they are created."
                  : undefined
              }
              createAction={
                allowCreate ? (
                  <Button variant="primary" onClick={() => setCreateOpen(true)}>
                    Add program
                  </Button>
                ) : undefined
              }
            />
          ) : (
            /* F3: real @vjal/ui ProTable-1 recipe export under Page-1 shell. */
            <ProTable
              collection={collection}
              primaryAction={
                allowCreate ? (
                  <Button variant="primary" onClick={() => setCreateOpen(true)}>
                    Add program
                  </Button>
                ) : undefined
              }
              className="p-0"
              tableProps={{
                caption: "Programs in the current view",
                emptyTitle: "No programs match your search",
                emptyMessage: "Clear or change the filters above to widen the view.",
                tableClassName: "min-w-0 md:min-w-[36rem]",
              }}
              stableChipNames
            />
          )}
        </div>

      {allowCreate && (
        <ProgramFormModal
          open={createOpen}
          mode="create"
          onClose={() => {
            setCreateOpen(false);
            mutations.createProgram.reset();
          }}
          onSubmit={submitCreate}
          submitting={mutations.createProgram.isPending}
          submitError={mutations.createProgram.error}
        />
      )}

      {allowUpdate && (
        <>
          <ProgramFormModal
            open={editTarget !== null}
            mode="edit"
            program={editTarget}
            onClose={() => {
              setEditTarget(null);
              mutations.updateProgram.reset();
            }}
            onSubmit={submitEdit}
            submitting={mutations.updateProgram.isPending}
            submitError={mutations.updateProgram.error}
          />
          <ProgramLifecycleModal
            open={lifecycle !== null}
            action={lifecycle?.action ?? "archive"}
            program={lifecycle?.program ?? null}
            onClose={() => {
              setLifecycle(null);
              mutations.updateProgram.reset();
            }}
            onConfirm={submitLifecycle}
            submitting={mutations.updateProgram.isPending}
            submitError={mutations.updateProgram.error}
          />
        </>
      )}
      </section>
    </PageFrame>
  );
}

/** Build the create body — only the fields the operator set (snake_case ids, integer-coerced for the live API). */
function programCreateBody(values: ProgramFormValues): Record<string, unknown> {
  const body: Record<string, unknown> = { name: values.name, status: values.status };
  if (values.companyId) body.company_id = asLiveApiId(values.companyId);
  if (values.coachId) body.coach_user_id = asLiveApiId(values.coachId);
  return body;
}

/** Build the edit body — name + status always; ids only when set (integer-coerced for the live API). */
function programEditBody(values: ProgramFormValues): Record<string, unknown> {
  const body: Record<string, unknown> = { name: values.name, status: values.status };
  if (values.companyId) body.company_id = asLiveApiId(values.companyId);
  if (values.coachId) body.coach_user_id = asLiveApiId(values.coachId);
  return body;
}
